import { useAppSelector } from '@app/store/store';
import { Switch } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ColorPicker } from '../../../../../../components/color-presets/color-picker';
import { expandHexColor } from '../../../../../../components/color-presets/utils';
import { CaptionOptions } from '../../../../../../components/icons/caption-options';
import { Palette } from '../../../../../../components/icons/palette';
import { DEFAULT_ACTIVE_WORD_COLOR } from '../../../../../../const';
import type {
  AdvancedEditCaptions,
  BrandingPreset,
  CaptionPosition,
} from '../../../../../../domain';
import { DEFAULT_ADVANCED_EDIT_CAPTIONS_ENABLED } from '../../../../../../domain';
import { useRecordingAnalytics } from '../../../../../../hooks';
import {
  selectActiveEditAspectRatio,
  selectActiveEditBackgroundColor,
  selectActiveEditCaptions,
  selectActiveEditPresetId,
  selectColorPresets,
  selectColorPresetsStatus,
} from '../../../../../../selector';
import {
  PrimaryColors,
  SecondaryColors,
  TertiaryColors,
} from '../../../../../../theme/constants';
import {
  CAPTION_FONT_OPTIONS,
  getClipPresetActiveWordColor,
  getClipPresetBackgroundColor,
  getClipPresetCaptionsColor,
  getCurrentPreset,
  getDefaultPreset,
} from '../../../../../../utils';
import { EditPresetSelector } from './edit-preset-selector';
import { useEditDraftUpdate } from './use-edit-draft-update';

const captionPositionOptions: { id: CaptionPosition; labelKey: string }[] = [
  { id: 'top', labelKey: 'content-recording.appearance.captions.position.top' },
  {
    id: 'center',
    labelKey: 'content-recording.appearance.captions.position.center',
  },
  {
    id: 'bottom',
    labelKey: 'content-recording.appearance.captions.position.bottom',
  },
];

const PositionIcon = ({
  position,
  isActive,
}: {
  position: CaptionPosition;
  isActive: boolean;
}) => (
  <div className="flex h-[22px] w-[25px] items-center justify-center">
    <div className="flex flex-col gap-[3px] items-start justify-center rounded-[2px]">
      {(['top', 'center', 'bottom'] as const).map(pos => (
        <div
          key={pos}
          className={cx('w-[11px] rounded-[1px]', {
            'h-[3px]': position === pos,
            'h-[2px]': position !== pos,
            'bg-spring-neutral-b2': position === pos && isActive,
            'bg-spring-neutral-b4': !(position === pos && isActive),
          })}
        />
      ))}
    </div>
  </div>
);

type EditAppearanceControlsProps = {
  disabled?: boolean;
};

export const EditAppearanceControls = ({
  disabled = false,
}: EditAppearanceControlsProps) => {
  const { t } = useTranslation();
  const { trackRecordingEvent } = useRecordingAnalytics();
  const { currentDraft, updateDraft } = useEditDraftUpdate();
  const presets = useAppSelector(selectColorPresets);
  const presetsStatus = useAppSelector(selectColorPresetsStatus);
  const aspectRatio = useAppSelector(selectActiveEditAspectRatio);
  const captions = useAppSelector(selectActiveEditCaptions);
  const backgroundColor = useAppSelector(selectActiveEditBackgroundColor);

  const [isFontListExpanded, setIsFontListExpanded] = useState(false);
  const [lastUsedBackgroundColor, setLastUsedBackgroundColor] = useState<
    string | null
  >(null);
  const [highlightColorInput, setHighlightColorInput] = useState(
    DEFAULT_ACTIVE_WORD_COLOR,
  );

  const defaultPreset = useMemo(() => getDefaultPreset(presets), [presets]);
  const selectedPresetId = useAppSelector(selectActiveEditPresetId);
  const isBlurMode = backgroundColor === null;
  const isPortrait = aspectRatio === '9_16';
  const captionsEnabled = captions.enabled;
  const currentPreset = useMemo(
    () => getCurrentPreset(selectedPresetId, presets),
    [selectedPresetId, presets],
  );

  const themeColors = useMemo(() => {
    const config = currentPreset?.config ?? {};
    const keys = [...PrimaryColors, ...SecondaryColors, ...TertiaryColors];
    return keys
      .map(key => config[key] as string)
      .filter(
        (color): color is string =>
          typeof color === 'string' && color.startsWith('#'),
      );
  }, [currentPreset?.config]);

  const portraitBgColor =
    (typeof backgroundColor === 'string' ? backgroundColor : null) ||
    lastUsedBackgroundColor ||
    getClipPresetBackgroundColor(selectedPresetId, presets) ||
    '#000000';

  const applyPreset = useCallback(
    (preset: BrandingPreset) => {
      if (!currentDraft) return;

      const activeWordColor = getClipPresetActiveWordColor(preset.id, presets);
      const backgroundFromPreset = getClipPresetBackgroundColor(
        preset.id,
        presets,
      );
      if (backgroundFromPreset) {
        setLastUsedBackgroundColor(backgroundFromPreset);
      }

      const isBlur = currentDraft.backgroundColor === null;
      const newBackgroundColor = isBlur
        ? null
        : backgroundFromPreset ??
          getClipPresetBackgroundColor(preset.id, presets) ??
          '#000000';

      const newCaptions: AdvancedEditCaptions = currentDraft.captions.enabled
        ? {
            ...currentDraft.captions,
            preset_id: preset.id,
            highlight_color: activeWordColor,
            captions_color: getClipPresetCaptionsColor(preset.id, presets),
          }
        : currentDraft.captions;

      if (currentDraft.captions.enabled) {
        setHighlightColorInput(activeWordColor.toUpperCase());
      }

      updateDraft(draft => ({
        ...draft,
        presetId: preset.id,
        backgroundColor: newBackgroundColor,
        captions: newCaptions,
      }));
      trackRecordingEvent('ch_creator_lab_appearance_tab_brand_selected', {
        brandName: preset.isPredefined ? preset.id : preset.name,
      });
    },
    [currentDraft, presets, trackRecordingEvent, updateDraft],
  );

  const handleBlurToggle = useCallback(
    (blur: boolean) => {
      updateDraft(draft => {
        if (blur) {
          if (typeof draft.backgroundColor === 'string') {
            setLastUsedBackgroundColor(draft.backgroundColor);
          }
          return { ...draft, backgroundColor: null };
        }
        const restore =
          lastUsedBackgroundColor ||
          getClipPresetBackgroundColor(selectedPresetId, presets) ||
          '#000000';
        return { ...draft, backgroundColor: restore };
      });
      trackRecordingEvent('ch_creator_lab_appearance_tab_background_selected', {
        background: blur ? 'blur' : 'color',
      });
    },
    [
      lastUsedBackgroundColor,
      presets,
      selectedPresetId,
      trackRecordingEvent,
      updateDraft,
    ],
  );

  const handleBackgroundColorChange = useCallback(
    (color: string) => {
      setLastUsedBackgroundColor(color);
      updateDraft(draft => ({
        ...draft,
        backgroundColor: color,
      }));
    },
    [updateDraft],
  );

  const handleCaptionsToggle = useCallback(
    (enabled: boolean) => {
      if (!currentDraft) return;

      if (!enabled) {
        updateDraft(draft => ({ ...draft, captions: { enabled: false } }));
      } else {
        const presetId =
          currentDraft.presetId ??
          (currentDraft.captions.enabled
            ? currentDraft.captions.preset_id
            : undefined) ??
          defaultPreset?.id ??
          '';
        const activeWordColor = getClipPresetActiveWordColor(presetId, presets);
        const captionsColor = getClipPresetCaptionsColor(presetId, presets);
        setHighlightColorInput(activeWordColor.toUpperCase());
        updateDraft(draft => ({
          ...draft,
          presetId,
          captions: {
            ...DEFAULT_ADVANCED_EDIT_CAPTIONS_ENABLED,
            preset_id: presetId,
            highlight_color: activeWordColor,
            captions_color: captionsColor,
          },
        }));
      }

      trackRecordingEvent('ch_creator_lab_appearance_tab_captions_toggle', {
        toggle: enabled ? 'ON' : 'OFF',
      });
    },
    [
      currentDraft,
      defaultPreset?.id,
      presets,
      trackRecordingEvent,
      updateDraft,
    ],
  );

  const handlePositionChange = useCallback(
    (position: CaptionPosition) => {
      if (!captionsEnabled) return;
      updateDraft(draft => {
        if (!draft.captions.enabled) return draft;
        return {
          ...draft,
          captions: { ...draft.captions, captions_position: position },
        };
      });
    },
    [captionsEnabled, updateDraft],
  );

  const handleFontSelect = useCallback(
    (fontId: string) => {
      if (!captionsEnabled) return;
      updateDraft(draft => {
        if (!draft.captions.enabled) return draft;
        return {
          ...draft,
          captions: { ...draft.captions, captions_style: fontId },
        };
      });
      setIsFontListExpanded(false);
    },
    [captionsEnabled, updateDraft],
  );

  const commitHighlightColor = useCallback(
    (newColor: string, trackAnalytics = true) => {
      if (!captionsEnabled) return;
      const normalized = newColor.toUpperCase();
      updateDraft(draft => {
        if (!draft.captions.enabled) return draft;
        return {
          ...draft,
          captions: { ...draft.captions, highlight_color: normalized },
        };
      });
      setHighlightColorInput(normalized);
      if (trackAnalytics) {
        const isThemeColor = themeColors.some(
          c => c.toUpperCase() === normalized,
        );
        trackRecordingEvent('ch_creator_lab_appearance_active_word_color', {
          themeColor: isThemeColor ? 'yes' : 'no',
          colorCode: normalized,
        });
      }
    },
    [captionsEnabled, themeColors, trackRecordingEvent, updateDraft],
  );

  useEffect(() => {
    if (captions.enabled) {
      setHighlightColorInput(captions.highlight_color.toUpperCase());
    }
  }, [captions]);

  const handleHighlightColorInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!captionsEnabled) return;
      const value = e.target.value.toUpperCase();
      setHighlightColorInput(value);
      if (/^#[0-9A-F]{6}$/i.test(value)) {
        commitHighlightColor(value, false);
      }
    },
    [captionsEnabled, commitHighlightColor],
  );

  const handleHighlightColorInputBlur = useCallback(() => {
    if (!captionsEnabled) return;
    if (/^#[0-9A-F]{3}$/i.test(highlightColorInput)) {
      commitHighlightColor(expandHexColor(highlightColorInput));
      return;
    }
    if (!/^#[0-9A-F]{6}$/i.test(highlightColorInput) && captions.enabled) {
      setHighlightColorInput(captions.highlight_color.toUpperCase());
    }
  }, [captions, captionsEnabled, commitHighlightColor, highlightColorInput]);

  if (!currentDraft) {
    return null;
  }

  const selectedFont =
    CAPTION_FONT_OPTIONS.find(
      f => captionsEnabled && f.id === captions.captions_style,
    ) || CAPTION_FONT_OPTIONS[0];

  const highlightColor = captionsEnabled
    ? captions.highlight_color
    : DEFAULT_ACTIVE_WORD_COLOR;

  if (isFontListExpanded && captionsEnabled) {
    return (
      <div
        className={cx(
          'flex h-full flex-col px-4 pt-5 [scrollbar-width:thin] [scrollbar-color:#696e7c_transparent]',
          disabled && 'pointer-events-none opacity-60',
        )}
      >
        <button
          type="button"
          onClick={() => setIsFontListExpanded(false)}
          disabled={disabled}
          className="mb-3 text-left text-[14px] font-medium text-white"
        >
          {t('content-recording.appearance.captions.font-style.label')}
        </button>
        <div className="flex flex-1 flex-col gap-2 overflow-y-auto">
          {CAPTION_FONT_OPTIONS.map(font => (
            <button
              key={font.id}
              type="button"
              onClick={() => handleFontSelect(font.id)}
              disabled={disabled}
              className={cx(
                'w-full rounded-[8px] px-4 py-3 text-center transition-colors hover:bg-spring-neutral-b0/10',
                captions.captions_style === font.id && 'bg-spring-neutral-b4',
              )}
              style={{
                fontFamily: font.fontFamily,
                fontWeight: font.fontWeight,
              }}
            >
              <span className="text-[20px] text-spring-neutral-b3">
                {font.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx(
        'flex h-full flex-col px-4 [scrollbar-width:thin] [scrollbar-color:#696e7c_transparent]',
        disabled && 'pointer-events-none opacity-60',
      )}
      {...(disabled && { 'aria-disabled': true })}
    >
      <div className="flex items-center gap-4 pb-2 pt-1">
        <Palette className="size-5 shrink-0 text-white" />
        <h2 className="m-0 text-[17px] font-inter font-medium leading-5 text-white">
          {t('content-recording.appearance.header')}
        </h2>
      </div>

      <div className="mb-3 mt-3 flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          <p className="m-0 text-[14px] font-inter font-medium leading-5 text-white">
            {t('content-recording.appearance.brand.label')}
          </p>
          <p className="m-0 text-[12px] font-inter font-normal leading-[18px] text-spring-neutral-b3">
            {t('content-recording.appearance.brand.description')}
          </p>
          {presetsStatus === 'processing' || presetsStatus === 'idle' ? (
            <div className="h-[68px] w-full animate-pulse rounded-[10px] bg-spring-neutral-b0/10" />
          ) : (
            <EditPresetSelector
              presets={presets}
              selectedPresetId={selectedPresetId}
              onPresetSelect={applyPreset}
              disabled={disabled}
            />
          )}
        </div>

        {isPortrait && (
          <div className="flex flex-col gap-4">
            <p className="m-0 text-[14px] font-medium text-white">
              {t('content-recording.appearance.video-background.label')}
            </p>
            <div className="flex gap-[12px]">
              <button
                type="button"
                onClick={() => handleBlurToggle(true)}
                disabled={disabled}
                className={cx(
                  'flex flex-1 flex-col items-center rounded-[8px] px-[6px] pt-2 pb-1 transition-colors',
                  isBlurMode && 'bg-spring-neutral-b5',
                )}
              >
                <div className="mb-1 h-[32px] w-[25px] overflow-hidden rounded-[8px]">
                  <div className="h-full w-full rounded-[8px] bg-blur-gradient blur-[8px]" />
                </div>
                <p className="m-0 text-[14px] font-semibold text-white">
                  {t('content-recording.appearance.video-background.blur')}
                </p>
              </button>
              <button
                type="button"
                onClick={() => handleBlurToggle(false)}
                disabled={disabled}
                className={cx(
                  'flex flex-1 flex-col items-center rounded-[8px] px-[6px] pt-2 pb-1 transition-colors',
                  !isBlurMode && 'bg-spring-neutral-b5',
                )}
              >
                <div className="relative mb-1 h-[32px] w-[25px] overflow-visible rounded-[8px]">
                  <ColorPicker
                    value={portraitBgColor}
                    onChange={handleBackgroundColorChange}
                    title={t(
                      'content-recording.appearance.video-background.color',
                    )}
                    shape="rectangle"
                    themeColors={themeColors}
                    disabled={disabled}
                  />
                </div>
                <p className="m-0 text-[14px] font-semibold text-white">
                  {t('content-recording.appearance.video-background.color')}
                </p>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* TODO: gate landscape captions behind a flag or disable the toggle when
          aspectRatio === '16_9' until the renderer team confirms that the monolith
          video processor burns captions on 16:9 exports. Without confirmation,
          users enabling captions on a landscape edit will see them in the live
          preview but they will be silently absent from the exported video. */}
      <div className="my-5 flex items-center gap-4">
        <CaptionOptions className="size-5 shrink-0 text-white" />
        <p className="m-0 flex-1 text-[17px] font-inter font-medium leading-[25px] tracking-[-0.2px] text-white">
          {t('content-recording.appearance.captions.label')}
        </p>
        <Switch
          checked={captionsEnabled}
          onChange={handleCaptionsToggle}
          disabled={disabled}
          className={cx(
            'relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 disabled:cursor-not-allowed disabled:opacity-60',
            captionsEnabled ? 'bg-spring-primary' : 'bg-spring-neutral-b3',
          )}
        >
          <span className="sr-only">
            {t('content-recording.appearance.captions.label')}
          </span>
          <span
            aria-hidden
            className={cx(
              'pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
              captionsEnabled ? 'translate-x-3' : 'translate-x-0',
            )}
          />
        </Switch>
      </div>

      {captionsEnabled && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <p className="m-0 text-[14px] font-medium text-white">
              {t('content-recording.appearance.captions.position.label')}
            </p>
            <div className="flex gap-3">
              {captionPositionOptions.map(({ id, labelKey }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => handlePositionChange(id)}
                  disabled={disabled}
                  className={cx(
                    'flex flex-1 flex-col items-center rounded-[8px] py-1 transition-colors',
                    captions.captions_position === id && 'bg-spring-neutral-b5',
                  )}
                >
                  <PositionIcon
                    position={id}
                    isActive={captions.captions_position === id}
                  />
                  <p className="m-0 text-[14px] font-semibold text-white">
                    {t(labelKey)}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <p className="m-0 text-[14px] font-medium text-white">
              {t('content-recording.appearance.captions.font-style.label')}
            </p>
            <button
              type="button"
              onClick={() => setIsFontListExpanded(true)}
              disabled={disabled}
              className="flex w-full items-center justify-between"
            >
              <span
                className="flex-1 text-center text-[20px] text-spring-neutral-b2"
                style={{
                  fontFamily: selectedFont.fontFamily,
                  fontWeight: selectedFont.fontWeight,
                }}
              >
                {selectedFont.name}
              </span>
            </button>
          </div>

          <div className="flex flex-col gap-3">
            <p className="m-0 text-[14px] font-medium leading-5 text-white">
              {t(
                'content-recording.appearance.captions.active-word-color.label',
              )}
            </p>
            <div className="flex items-center gap-2">
              <div className="relative h-[32px] w-[32px] overflow-visible rounded-[8px]">
                <ColorPicker
                  value={highlightColor}
                  onChange={commitHighlightColor}
                  title={t(
                    'content-recording.appearance.captions.active-word-color.label',
                  )}
                  shape="rectangle"
                  placement="bottom-start"
                  themeColors={themeColors}
                  disabled={disabled}
                />
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={highlightColorInput}
                  onChange={handleHighlightColorInputChange}
                  onBlur={handleHighlightColorInputBlur}
                  disabled={disabled}
                  aria-label={t(
                    'content-recording.appearance.captions.active-word-color.label',
                  )}
                  className="h-[26px] w-[101px] rounded-[4px] border-none bg-spring-neutral-b5/90 text-center text-[14px] font-semibold leading-[21px] text-white outline-none"
                  maxLength={7}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
